if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.mobileCurrentIndex = 0;
        this.mobileIsSnapping = false;
        this.mobileScrollRaf = null;
        this.mobileGestureStartIndex = null;
        this.mobileTouchMoves = [];
        this.mobileStartX = 0;
        this.mobileStartY = 0;
        this.mobileLockIndex = null;
        this.mobileLockUntil = 0;
        this.mobileUsesManualDrag = /Android/i.test(navigator.userAgent);
        this.mobileSlider = null;
        this.mobileBound = false;
        this.onMobileResize = this.onMobileResize.bind(this);
      }

      connectedCallback() {
        this.setupThumbnails();
        this.setupMobileGallery();
        this.mobileMql.addEventListener('change', this.onMobileResize);
      }

      disconnectedCallback() {
        this.mobileMql.removeEventListener('change', this.onMobileResize);
        if (this.mobileResizeObserver) {
          this.mobileResizeObserver.disconnect();
        }
      }

      onMobileResize() {
        if (this.mobileMql.matches) {
          this.setupMobileGallery();
        } else if (this.mobileSlider) {
          this.mobileSlider.classList.remove('product__media-list--controlled');
          this.mobileBound = false;
          this.mobileSlider = null;
        }
      }

      setupThumbnails() {
        if (!this.elements.viewer || !this.elements.thumbnails || this.thumbnailsBound) return;

        this.thumbnailsBound = true;
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      isSlideVisible(slide) {
        return slide && window.getComputedStyle(slide).display !== 'none';
      }

      getMobileSlides(slider) {
        return Array.from(slider.querySelectorAll('.product__media-item')).filter((slide) => this.isSlideVisible(slide));
      }

      getSlideLeft(slider, slides, index) {
        if (!slides[index]) return 0;
        return slides[index].offsetLeft;
      }

      getNearestMobileSlideIndex(slider, slides) {
        const scrollLeft = slider.scrollLeft;
        let nearestIndex = 0;
        let minDistance = Infinity;

        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - scrollLeft);
          if (distance < minDistance) {
            minDistance = distance;
            nearestIndex = index;
          }
        });

        return nearestIndex;
      }

      getMobileSlideBounds(slider, slides, startIndex) {
        const prevIndex = Math.max(0, startIndex - 1);
        const nextIndex = Math.min(slides.length - 1, startIndex + 1);
        return {
          minLeft: this.getSlideLeft(slider, slides, prevIndex),
          maxLeft: this.getSlideLeft(slider, slides, nextIndex),
        };
      }

      clampMobileScrollRange(slider) {
        if (this.mobileGestureStartIndex === null) return;

        const slides = this.getMobileSlides(slider);
        if (!slides.length) return;

        const { minLeft, maxLeft } = this.getMobileSlideBounds(slider, slides, this.mobileGestureStartIndex);
        if (slider.scrollLeft < minLeft) {
          slider.scrollLeft = minLeft;
        } else if (slider.scrollLeft > maxLeft) {
          slider.scrollLeft = maxLeft;
        }
      }

      getMobileSwipeVelocity() {
        const moves = this.mobileTouchMoves;
        if (moves.length < 2) return 0;

        const first = moves[0];
        const last = moves[moves.length - 1];
        const elapsed = last.t - first.t;

        if (elapsed <= 0) return 0;
        return (last.x - first.x) / elapsed;
      }

      finishMobileGesture(slider, event) {
        if (this.mobileGestureStartIndex === null) return;

        const slides = this.getMobileSlides(slider);
        if (!slides.length) {
          this.resetMobileGesture();
          return;
        }

        const startIndex = this.mobileGestureStartIndex;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.mobileStartX;
        const deltaY = touch.clientY - this.mobileStartY;
        const velocityX = this.getMobileSwipeVelocity();
        const displacementThreshold = 40;
        const velocityThreshold = 0.25;

        let direction = 0;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          if (Math.abs(velocityX) >= velocityThreshold) {
            direction = velocityX < 0 ? 1 : -1;
          } else if (Math.abs(deltaX) >= displacementThreshold) {
            direction = deltaX < 0 ? 1 : -1;
          }
        }

        const targetIndex = Math.max(0, Math.min(slides.length - 1, startIndex + direction));
        this.resetMobileGesture();
        this.mobileGoToSlide(slider, targetIndex);
        if (this.mobileUsesManualDrag) {
          this.startMobileSlideLock(slider, targetIndex);
        }
      }

      resetMobileGesture() {
        this.mobileGestureStartIndex = null;
        this.mobileTouchMoves = [];
      }

      startMobileSlideLock(slider, index) {
        this.mobileLockIndex = index;
        this.mobileLockUntil = Date.now() + (this.mobileUsesManualDrag ? 900 : 400);
        this.enforceMobileSlideLock(slider);
      }

      clearMobileSlideLock() {
        this.mobileLockIndex = null;
        this.mobileLockUntil = 0;
      }

      enforceMobileSlideLock(slider) {
        if (this.mobileLockIndex === null || Date.now() > this.mobileLockUntil) {
          this.clearMobileSlideLock();
          return false;
        }

        const slides = this.getMobileSlides(slider);
        if (!slides.length || !slides[this.mobileLockIndex]) {
          this.clearMobileSlideLock();
          return false;
        }

        const targetLeft = this.getSlideLeft(slider, slides, this.mobileLockIndex);
        if (Math.abs(slider.scrollLeft - targetLeft) > 1) {
          slider.scrollLeft = targetLeft;
        }

        return true;
      }

      handleAndroidTouchMove(slider, event) {
        if (this.mobileGestureStartIndex === null) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - this.mobileStartX;
        const deltaY = touch.clientY - this.mobileStartY;

        if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 12) {
          slider.style.scrollSnapType = '';
          this.resetMobileGesture();
          return;
        }

        if (Math.abs(deltaX) <= Math.abs(deltaY) && Math.abs(deltaX) < 10) return;

        event.preventDefault();

        const slides = this.getMobileSlides(slider);
        if (!slides.length) return;

        const startLeft = this.getSlideLeft(slider, slides, this.mobileGestureStartIndex);
        const { minLeft, maxLeft } = this.getMobileSlideBounds(slider, slides, this.mobileGestureStartIndex);
        slider.style.scrollSnapType = 'none';
        slider.scrollLeft = Math.max(minLeft, Math.min(maxLeft, startLeft - deltaX));
      }

      syncMobileSlideFromScroll(slider) {
        if (this.mobileIsSnapping || this.mobileGestureStartIndex !== null) return;

        const slides = this.getMobileSlides(slider);
        if (!slides.length) return;

        const nearestIndex = this.getNearestMobileSlideIndex(slider, slides);
        if (nearestIndex === this.mobileCurrentIndex) return;

        this.mobileCurrentIndex = nearestIndex;
        const activeSlide = slides[nearestIndex];
        slider.querySelectorAll('.product__media-item').forEach((slide) => {
          slide.classList.toggle('is-active', slide === activeSlide);
        });
        this.updateMobileButtons(slider);
      }

      updateMobileButtons(slider) {
        const slides = this.getMobileSlides(slider);
        const prevButton = this.elements.viewer?.querySelector('.slider-button--prev');
        const nextButton = this.elements.viewer?.querySelector('.slider-button--next');

        if (prevButton) {
          prevButton.removeAttribute('disabled');
          if (this.mobileCurrentIndex <= 0) {
            prevButton.setAttribute('aria-disabled', 'true');
            prevButton.classList.add('slider-button--disabled');
          } else {
            prevButton.removeAttribute('aria-disabled');
            prevButton.classList.remove('slider-button--disabled');
          }
        }

        if (nextButton) {
          nextButton.removeAttribute('disabled');
          if (this.mobileCurrentIndex >= slides.length - 1) {
            nextButton.setAttribute('aria-disabled', 'true');
            nextButton.classList.add('slider-button--disabled');
          } else {
            nextButton.removeAttribute('aria-disabled');
            nextButton.classList.remove('slider-button--disabled');
          }
        }

        const currentCounter = this.elements.viewer?.querySelector('.slider-counter--current');
        const totalCounter = this.elements.viewer?.querySelector('.slider-counter--total');
        if (currentCounter && slides.length) {
          currentCounter.textContent = this.mobileCurrentIndex + 1;
        }
        if (totalCounter && slides.length) {
          totalCounter.textContent = slides.length;
        }
      }

      keepMobileButtonsClickable(button, slider) {
        if (!button || button.dataset.mobilePathed) return;
        button.dataset.mobilePathed = 'true';

        new MutationObserver(() => {
          if (button.hasAttribute('disabled')) {
            button.removeAttribute('disabled');
            this.updateMobileButtons(slider);
          }
        }).observe(button, { attributes: true, attributeFilter: ['disabled'] });
      }

      mobileGoToSlide(slider, index) {
        const slides = this.getMobileSlides(slider);
        if (!slides.length) return;

        const clampedIndex = Math.max(0, Math.min(index, slides.length - 1));
        this.mobileCurrentIndex = clampedIndex;
        this.mobileIsSnapping = true;

        const activeSlide = slides[clampedIndex];
        slider.querySelectorAll('.product__media-item').forEach((slide) => {
          slide.classList.toggle('is-active', slide === activeSlide);
        });

        slider.scrollTo({
          left: this.getSlideLeft(slider, slides, clampedIndex),
          behavior: 'auto',
        });

        this.updateMobileButtons(slider);

        window.requestAnimationFrame(() => {
          this.mobileIsSnapping = false;
        });
      }

      bindMobileGestureEvents(slider) {
        if (slider.dataset.mobileGesturesBound) return;
        slider.dataset.mobileGesturesBound = 'true';

        const trackTouch = (event) => {
          if (event.touches.length !== 1) return;
          this.mobileTouchMoves.push({ x: event.touches[0].clientX, t: Date.now() });
          if (this.mobileTouchMoves.length > 8) {
            this.mobileTouchMoves.shift();
          }
        };

        slider.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            if (event.target.closest('.product__media-toggle')) return;

            const slides = this.getMobileSlides(slider);
            if (!slides.length) return;

            this.clearMobileSlideLock();
            this.resetMobileGesture();
            this.mobileGestureStartIndex = this.mobileCurrentIndex;
            this.mobileStartX = event.touches[0].clientX;
            this.mobileStartY = event.touches[0].clientY;
            this.mobileTouchMoves = [{ x: this.mobileStartX, t: Date.now() }];

            if (this.mobileUsesManualDrag) {
              slider.style.scrollSnapType = 'none';
            }

            slider.scrollTo({
              left: this.getSlideLeft(slider, slides, this.mobileCurrentIndex),
              behavior: 'auto',
            });
          },
          { passive: true, capture: true }
        );

        slider.addEventListener(
          'touchmove',
          (event) => {
            if (this.mobileGestureStartIndex === null) return;
            trackTouch(event);

            if (this.mobileUsesManualDrag) {
              this.handleAndroidTouchMove(slider, event);
              return;
            }

            const deltaX = event.touches[0].clientX - this.mobileStartX;
            const deltaY = event.touches[0].clientY - this.mobileStartY;
            if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 12) {
              this.resetMobileGesture();
              return;
            }

            slider.style.scrollSnapType = 'none';
            this.clampMobileScrollRange(slider);
          },
          { passive: !this.mobileUsesManualDrag, capture: true }
        );

        slider.addEventListener(
          'touchend',
          (event) => {
            if (this.mobileGestureStartIndex === null) return;
            slider.style.scrollSnapType = '';
            this.mobileTouchMoves.push({
              x: event.changedTouches[0].clientX,
              t: Date.now(),
            });
            this.finishMobileGesture(slider, event);
          },
          { passive: true, capture: true }
        );

        slider.addEventListener(
          'touchcancel',
          (event) => {
            if (this.mobileGestureStartIndex === null) return;
            slider.style.scrollSnapType = '';
            this.finishMobileGesture(slider, event);
          },
          { passive: true, capture: true }
        );

        slider.addEventListener(
          'scroll',
          () => {
            window.cancelAnimationFrame(this.mobileScrollRaf);
            this.mobileScrollRaf = window.requestAnimationFrame(() => {
              if (this.enforceMobileSlideLock(slider)) return;

              if (this.mobileGestureStartIndex !== null) {
                if (!this.mobileUsesManualDrag) {
                  this.clampMobileScrollRange(slider);
                }
                return;
              }

              this.syncMobileSlideFromScroll(slider);
            });
          },
          { passive: true }
        );
      }

      setupMobileGallery() {
        if (!this.mobileMql.matches || !this.elements.viewer || this.mobileBound) return;

        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;

        const slides = this.getMobileSlides(slider);
        if (slides.length < 2) {
          if (!this.mobileWaitObserver) {
            this.mobileWaitObserver = new ResizeObserver(() => {
              if (this.getMobileSlides(slider).length >= 2) {
                this.mobileWaitObserver.disconnect();
                this.mobileWaitObserver = null;
                this.setupMobileGallery();
              }
            });
            this.mobileWaitObserver.observe(slider);
          }
          return;
        }

        this.mobileBound = true;
        this.mobileSlider = slider;
        slider.classList.add('product__media-list--controlled');

        const prevButton = this.elements.viewer.querySelector('.slider-button--prev');
        const nextButton = this.elements.viewer.querySelector('.slider-button--next');
        this.keepMobileButtonsClickable(prevButton, slider);
        this.keepMobileButtonsClickable(nextButton, slider);

        window.requestAnimationFrame(() => {
          this.mobileGoToSlide(slider, this.mobileCurrentIndex);
        });

        this.bindMobileGestureEvents(slider);

        const buttonsContainer = this.elements.viewer.querySelector('.slider-buttons');
        if (buttonsContainer) {
          buttonsContainer.addEventListener(
            'click',
            (event) => {
              const prevBtn = event.target.closest('.slider-button--prev');
              const nextBtn = event.target.closest('.slider-button--next');

              if (prevBtn && !prevBtn.classList.contains('slider-button--disabled')) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.mobileGoToSlide(slider, this.mobileCurrentIndex - 1);
                return;
              }

              if (nextBtn && !nextBtn.classList.contains('slider-button--disabled')) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.mobileGoToSlide(slider, this.mobileCurrentIndex + 1);
              }
            },
            true
          );
        }

        this.mobileResizeObserver = new ResizeObserver(() => {
          this.mobileGoToSlide(slider, this.mobileCurrentIndex);
        });
        this.mobileResizeObserver.observe(slider);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        const activeSlide = activeMedia?.closest('.product__media-item');
        if (activeSlide) {
          this.elements.viewer.querySelectorAll('.product__media-item').forEach((slide) => {
            slide.classList.toggle('is-active', slide === activeSlide);
          });
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            if (this.mobileMql.matches && this.mobileSlider) {
              const slide = activeMedia.closest('.product__media-item');
              const slides = this.getMobileSlides(this.mobileSlider);
              const index = slides.indexOf(slide);
              if (index >= 0) {
                this.mobileGoToSlide(this.mobileSlider, index);
              } else {
                this.mobileGoToSlide(this.mobileSlider, 0);
              }
            } else {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img, .product__modal-opener--no-zoom img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
